import { useMemo } from 'react';
import BreakdownTable, {
  type BreakdownCell,
  type BreakdownCellContext,
} from '../ui/breakdown-tables/BreakdownTable';
import { useBreakdownCells } from './hooks/useBreakdownCells';
import {
  createDataCellRenderer,
  aggregateCellValuesWithYOY,
} from './utils/rebateAnalyticsUtils';
import { createMonthMetadata } from '@/utils/rebateForecastingUtils';
import {
  generateMonthColumnOrder,
  generateMonthSubColumnOrder,
} from '@/utils/breakdown-transforms/monthOrdering';
import {
  DimensionType,
  DIMENSION_LABELS,
  MultiYearForecastData,
  DimensionGroupingConfig,
} from '@/types/rebateAnalytics';

interface RebateAnalyticsTablePreviewProps {
  multiYearForecastData: MultiYearForecastData;
  primaryXDimension: DimensionType;
  secondaryXDimension: DimensionType | undefined;
  primaryYDimension: DimensionType | undefined;
  secondaryYDimension: DimensionType | undefined;
  useCumulative: boolean;
  supplierIdToName: Map<string, string>;
  divisionIdToName: Map<string, string>;
  ruleTagIdToName?: Map<string, string>;
  agreementTagIdToName?: Map<string, string>;
  supplierTagIdToName?: Map<string, string>;
  ruleTagsMap?: Record<string, string[]>;
  agreementTagsMap?: Record<string, string[]>;
  supplierTagsMap?: Record<string, string[]>;
  hasDuplicates: boolean;
  dimensionGroupingConfig?: Partial<
    Record<
      Exclude<DimensionType, DimensionType.TIME | DimensionType.RULE_TYPE>,
      DimensionGroupingConfig
    >
  >;
}

export function RebateAnalyticsTablePreview({
  multiYearForecastData,
  primaryXDimension,
  secondaryXDimension,
  primaryYDimension,
  secondaryYDimension,
  useCumulative,
  supplierIdToName,
  divisionIdToName,
  ruleTagIdToName,
  agreementTagIdToName,
  supplierTagIdToName,
  ruleTagsMap,
  agreementTagsMap,
  supplierTagsMap,
  hasDuplicates,
  dimensionGroupingConfig,
}: RebateAnalyticsTablePreviewProps) {
  // Generate preview data using the same hook as the actual table
  const previewData = useBreakdownCells({
    multiYearForecastData,
    primaryXDimension,
    secondaryXDimension,
    primaryYDimension,
    secondaryYDimension,
    dimensionGroupingConfig,
  });

  // Create month metadata from forecast data for cell renderer
  const monthMetadata = useMemo(() => {
    const forecastData = multiYearForecastData.currentYearData;
    if (!forecastData) {
      return [];
    }
    return createMonthMetadata(forecastData);
  }, [multiYearForecastData]);

  // Create cell renderer adapter
  const previewRenderDataCell = useMemo(() => {
    const currentYear = multiYearForecastData.currentYear;
    const previousYear = currentYear - 1;
    const originalRenderer = createDataCellRenderer(
      monthMetadata,
      currentYear,
      previousYear,
    );

    return (context: BreakdownCellContext) => {
      const { cell } = context;
      const metadata = cell.metadata as
        | {
            headerName?: string;
            isGroupLevel?: boolean;
          }
        | undefined;

      // Create mock old-style context for compatibility
      const mockOldContext = {
        header: {
          name: metadata?.headerName || cell.column,
        },
      };

      const legacyValue = cell.value as
        | number
        | { current: number; previous: number; delta: number }
        | null;
      return originalRenderer(legacyValue, mockOldContext);
    };
  }, [monthMetadata, multiYearForecastData]);

  // Get dimension labels for preview
  const previewGroupLabel = primaryYDimension
    ? DIMENSION_LABELS[primaryYDimension]
    : undefined;
  const previewSubRowLabel = secondaryYDimension
    ? DIMENSION_LABELS[secondaryYDimension]
    : undefined;

  // Generate month ordering if primary X dimension is TIME
  const xDimOrder = useMemo(() => {
    if (primaryXDimension === DimensionType.TIME) {
      return generateMonthColumnOrder(previewData);
    }
    return undefined;
  }, [primaryXDimension, previewData]);

  const xDim2Order = useMemo(() => {
    if (secondaryXDimension === DimensionType.TIME) {
      return generateMonthSubColumnOrder(previewData);
    }
    return undefined;
  }, [secondaryXDimension, previewData]);

  // Expand all xdim1 (primary columns) and xdim2 (secondary columns) for preview
  // SubColumns are automatically visible when parent columns are expanded

  // Check if we're in the default state (only TIME selected, all others are defaults/none)
  const isDefaultState = useMemo(() => {
    return (
      primaryXDimension === DimensionType.TIME &&
      secondaryXDimension === undefined &&
      primaryYDimension === undefined &&
      secondaryYDimension === undefined
    );
  }, [
    primaryXDimension,
    secondaryXDimension,
    primaryYDimension,
    secondaryYDimension,
  ]);

  // Don't render if there are duplicates, default state, or no data
  if (hasDuplicates || isDefaultState || previewData.length === 0) {
    return null;
  }

  return (
    <div className="w-full h-full flex flex-col min-h-0">
      <div className="opacity-60 w-full flex-1 overflow-auto min-w-0">
        <div className="pointer-events-none min-w-fit pb-4">
          <BreakdownTable
            data={previewData}
            rowLabel={previewGroupLabel}
            subRowLabel={previewSubRowLabel}
            columnLabel={DIMENSION_LABELS[primaryXDimension]}
            subColumnLabel={
              secondaryXDimension
                ? DIMENSION_LABELS[secondaryXDimension]
                : undefined
            }
            title=""
            subtitle=""
            showCard={false}
            renderCell={previewRenderDataCell}
            aggregateCellValues={aggregateCellValuesWithYOY}
            showXDim1Total={false}
            showYDim1Total={true}
            maxHeight="100%"
            expandAllColumns={true}
            showColumnReferences={true}
            xDimOrder={xDimOrder}
            xDim2Order={xDim2Order}
          />
        </div>
      </div>
    </div>
  );
}
